import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { DocumentsPage } from "@/features/documents/components/documents-page";
import {
  downloadPatientDocument,
  type PatientDocumentDto,
  retractPatientDocument,
  uploadPatientRecord,
  usePatientDocuments,
} from "@/shared/edge/queries";
import { patientDomainIdFromProfile } from "@/shared/lib/patient-profile";

export const Route = createFileRoute("/_consented/documents/")({ component: Page });

function Page() {
  const { profile } = Route.useRouteContext();
  const patientId = patientDomainIdFromProfile(profile);
  const { documents, isLoading, isError } = usePatientDocuments({ patientId });
  const [downloadingId, setDownloadingId] = useState<string>();
  const [isUploading, setIsUploading] = useState(false);
  const [confirming, setConfirming] = useState<PatientDocumentDto>();
  const [retractingId, setRetractingId] = useState<PatientDocumentDto["id"]>();
  const [error, setError] = useState<string>();

  async function upload(file: File) {
    if (!patientId || isUploading) return;
    if (file.type !== "application/pdf" && !file.type.startsWith("image/")) {
      setError("Choose a PDF or image.");
      return;
    }
    setError(undefined);
    setIsUploading(true);
    try {
      await uploadPatientRecord(file);
    } catch {
    } finally {
      setIsUploading(false);
    }
  }

  async function retract(id: PatientDocumentDto["id"]) {
    if (retractingId) return;
    setError(undefined);
    setRetractingId(id);
    try {
      await retractPatientDocument(id);
    } catch {
    } finally {
      setConfirming(undefined);
      setRetractingId(undefined);
    }
  }

  return (
    <DocumentsPage
      documents={documents}
      isLoading={Boolean(patientId) && isLoading}
      error={
        !patientId
          ? "Your patient profile is unavailable. Please sign in again."
          : isError
            ? "Could not load your documents. Please reload the page."
            : error
      }
      canUpload={Boolean(patientId)}
      isUploading={isUploading}
      confirming={confirming}
      retractingId={retractingId}
      downloadingId={downloadingId}
      onDownload={(document) => {
        if (!document.binaryId) return;
        setDownloadingId(document.id);
        void downloadPatientDocument({ id: document.id, title: document.title })
          .catch(() => setError(`Could not download “${document.title}”. Please try again.`))
          .finally(() => setDownloadingId(undefined));
      }}
      onUpload={(file) => void upload(file)}
      onRequestRetract={setConfirming}
      onCancelRetract={() => {
        if (!retractingId) setConfirming(undefined);
      }}
      onConfirmRetract={(id) => void retract(id)}
    />
  );
}
